<script setup>
/**
 * The body of one Dining Profile entry — the member's own words, and for a
 * saved person, who they are and the card they photographed — drawn the same
 * way on every surface that shows one: a restaurant's My Notes, the notes
 * pop-up and the chat's note list (2026-09-23). The decisions behind it (whose
 * words these are, what a card's transcription is) live in utils/noteDisplay.js.
 *
 * A person reads: who they are, what the member said about them, then the
 * card (Kapil, 2026-09-23). Their words used to come first, so a note about
 * "him" sat above the line that says who he is.
 *
 * Each surface keeps its own actions and meta line; only the body is shared,
 * because that is where they must never disagree.
 */
import { computed } from 'vue'
import { isContactEntry, ownWords } from '@/utils/noteDisplay'
import ProfileContactLine from '@core/components/ProfileContactLine.vue'

const props = defineProps({
  entry: { type: Object, required: true },
  restaurantName: { type: String, default: '' },
  // The surface's own typography for the words.
  textClass: { type: String, default: '' },
})

const emit = defineEmits(['open-card'])

const words = computed(() => ownWords(props.entry, props.restaurantName))
const cardImageId = computed(() => (props.entry.source && props.entry.source.imageId) || null)
</script>

<template>
  <!--
    The `words` slot takes the words' place — an editor, when the member edits
    them in place — so the person's line and card stay where they are.
  -->
  <ProfileContactLine
    v-if="isContactEntry(entry) && entry.contact"
    :contact="entry.contact"
    :card-image-id="cardImageId"
    @open-card="emit('open-card', $event)"
  >
    <slot name="words">
      <p
        v-if="words"
        class="entry-body__text entry-body__text--under-person"
        :class="textClass"
      >
        {{ words }}
      </p>
    </slot>
  </ProfileContactLine>
  <slot
    v-else
    name="words"
  >
    <p
      v-if="words"
      class="entry-body__text"
      :class="textClass"
    >
      {{ words }}
    </p>
  </slot>
</template>

<style scoped>
.entry-body__text {
  margin: 0;
  white-space: pre-line;
}

.entry-body__text--under-person {
  margin-top: 4px;
}
</style>
